import React, { useState, useEffect, useContext, useRef } from 'react';
import { RFPApprovalRequest } from '../rfpApprovalRequest';
import { ComponentProps, DefaultArrayData } from '../../organisms/RFPComponent/RFPComponent.types';
import clsx from 'clsx';
import { StyledEventDetailsDiv } from './index.styles';
import { serverData } from '../../organisms/RFPComponent/context';
import axios from 'axios';
import { RadioButton } from '@marriott/mi-ui-library';

interface iprops {
  onDataChange: (data: { key: string; value: string }) => void;
  onClear: boolean;
  data: Array<DefaultArrayData>;
  index: number;
  submitted?: boolean;
  isTabletView?: boolean;
}

export const EventDetails: React.FC<iprops> = (props: iprops) => {
  const { jsonContent }: ComponentProps = useContext(serverData);
  const eventDetails = [...(jsonContent?.['instance']?.eventDetails?.fieldSet ?? [])];
  const uploadRef: React.RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);
  const labels = jsonContent?.['instance']?.labels ?? {};
  const [rfpPrepared, setRFPPrepared] = useState('');
  const [_rfpFile, setRFPFile] = useState('');

  const onClear = (flag: boolean) => {
    if (flag) {
      setRFPPrepared('');
      const rfpPreparedArr = Array.from(
        document.getElementsByClassName('rfpPrepared') as HTMLCollectionOf<HTMLElement>
      )[0]?.children;
      for (let i = 0; i < rfpPreparedArr?.length; i++) {
        if (rfpPreparedArr[i]?.children[0]) {
          (rfpPreparedArr[i].children[0] as HTMLInputElement).checked = false;
        }
      }
    }
  };

  const uploadAttachment = async (file: string | Blob, name: string, value: string) => {
    try {
      const formdata = new FormData();
      formdata.append('fileToUpload', file);
      const upload: { status: string } = await axios.post(`/phoenix-trc/v1/uploadAttachment`, formdata, {
        headers: {
          'content-type': 'multipart/form-data',
        },
      });
      if (upload?.status === 'FAILURE') {
        props.onDataChange({
          key: name,
          value: '',
        });
      } else {
        props.onDataChange({
          key: name,
          value: value,
        });
      }
    } catch (err) {
      props.onDataChange({
        key: name,
        value: '',
      });
    }
  };

  useEffect(() => {
    onClear(props.onClear);
  }, [props.data, props.onClear]);

  return (
    <StyledEventDetailsDiv data-component-name="m-trc-EventDetails" data-testid="trc-EventDetails">
      <div className="">
        <p className="mb-md-5 pb-2 pb-md-3 pt-5 t-title-alt-s t-rc-italic">
          {props.index}.{jsonContent?.['instance']?.eventDetails?.name}
        </p>
        <div className="eventDetails offset-xl-2 row">
          <p
            className={`t-overline-alt-medium t-rc-text-transform-none col-12 ${
              rfpPrepared === (labels?.yesLabel ?? 'Yes') ? 'col-xl-6' : 'col-xl-12'
            } col-md-8 p--xl-0 m-0 mb-xl-3 mb-4 mb-md-5`}
          >{`${eventDetails[0]?.fieldName}
            ${eventDetails[0]?.mandatory ? '*' : ''}`}</p>
          {rfpPrepared === (labels?.yesLabel ?? 'Yes') && (
            <p
              className={`t-overline-alt-medium t-rc-text-transform-none col-12 col-xl-6 col-md-8 d-none d-xl-block mb-4 mb-md-5 mb-xl-0`}
            >
              {`${eventDetails[1]?.fieldName}
            ${eventDetails[1]?.mandatory ? '*' : ''}`}
            </p>
          )}

          <div className="rfpPrepared d-flex col-12 col-xl-6 col-md-8 mb-4 mb-md-5 mb-xl-0">
            {eventDetails[0]?.inputOptions?.map((item: Record<string, string>) => {
              return (
                <RadioButton
                  radiobuttonId={`${eventDetails[0]?.name}_${item['option']}`}
                  radiobuttonName={eventDetails[0]?.name + item['option']}
                  radiobuttonLabel={item['option']}
                  disabled={props.submitted}
                  aria-label={`${eventDetails[0]?.name}_${item['option']}`}
                  ariaLabelForLink={`${eventDetails[0]?.name}_${item['option']}`}
                  className={clsx(
                    'm-radio-button-sm',
                    'pr-4',
                    `rfp-${eventDetails[0]?.name}`,
                    props?.data[0]?.isError ? 'error' : ''
                  )}
                  labelClassName="t-font-m"
                  checked={item['option'] === props?.data[0]?.value}
                  onChange={e => {
                    e && setRFPPrepared(e?.target.value);
                    props.onDataChange({
                      key: eventDetails[0]?.name,
                      value: e?.target?.value ? e?.target?.value : '',
                    });
                    if (e?.target.value && e?.target.value === (labels?.noLabel ?? 'No')) {
                      props.onDataChange({
                        key: eventDetails[0]?.name,
                        value: e?.target.value,
                      });
                    }
                  }}
                  onKeyDown={(event: React.KeyboardEvent) => {
                    if (event.key === 'Enter' || event.keyCode === 13) {
                      const target = event?.target as HTMLInputElement;
                      event && setRFPPrepared(target?.value);
                      props.onDataChange({
                        key: eventDetails[0]?.name,
                        value: target?.value ? target?.value : '',
                      });
                      if (target.value && target.value === (labels?.noLabel ?? 'No')) {
                        props.onDataChange({
                          key: eventDetails[0]?.name,
                          value: target.value,
                        });
                      }
                    }
                  }}
                  value={item['option']}
                  setTabIndex={-1}
                />
              );
            })}
          </div>
          <div
            className={clsx(
              'error-text t-font-xs col-12 col-xl-12 col-md-8',
              props?.data?.[0]?.isError ? 'd-block' : 'd-none'
            )}
          >
            {eventDetails[0]?.errorMessage}
          </div>

          {rfpPrepared === (labels?.yesLabel ?? 'Yes') && (
            <p className="t-overline-alt-medium t-rc-text-transform-none col-12 col-xl-6 col-md-8 d-block d-xl-none m-0  mb-4 mb-md-5 mb-xl-3">
              {`${eventDetails[1]?.fieldName}
            ${eventDetails[1]?.mandatory ? '*' : ''}`}
            </p>
          )}

          {rfpPrepared === (labels?.yesLabel ?? 'Yes') && (
            <div className="rfpFile col-12 col-xl-6 col-md-8  mb-4 mb-md-5 mb-xl-0">
              <span id={`rfp-${eventDetails[1]?.name}`}></span>
              <button
                className={`m-button-s rfpFile-btn ${props.submitted ? 'disabled' : ''}`}
                onClick={() => {
                  uploadRef?.current && uploadRef?.current.click();
                }}
              >
                {' '}
                {eventDetails[1]?.placeholder}
              </button>
              <input
                type="file"
                ref={uploadRef}
                id={eventDetails[1]?.name}
                aria-label="Select File"
                placeholder="Select File"
                className="rfpFile d-none"
                disabled={props.submitted}
                onChange={e => {
                  setRFPFile(e.target.value);
                  if (e.target.files && e.target.files.length > 0) {
                    const file: File | undefined = e.target.files[0];
                    if (file) {
                      uploadAttachment(file, eventDetails[1]?.name, e.target.value);
                    }
                  }
                }}
              />
              <span className="ml-2 file-name">{uploadRef?.current?.files && uploadRef?.current?.files[0]?.name}</span>
              <div
                className={clsx(
                  'error-text mt-3 t-font-xs col-12 col-xl-12 col-md-8',
                  props?.data[16]?.isError ? 'd-block' : 'd-none'
                )}
              >
                {eventDetails[1]?.errorMessage}
              </div>
            </div>
          )}
          {rfpPrepared === (labels?.noLabel ?? 'No') && (
            <div className="col-12 mt-4">
              <RFPApprovalRequest
                submitted={props.submitted ?? false}
                onDataChange={props.onDataChange}
                data={props.data}
                onClear={props.onClear}
                isTabletView={props?.isTabletView}
              />
            </div>
          )}
        </div>
      </div>
    </StyledEventDetailsDiv>
  );
};
